import React from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { Skeleton } from '../ui/Skeleton';
import { Alert } from '../ui/Alert';
import { Button } from '../ui/Button';

export interface QueryStateProps<T> {
  query: UseQueryResult<T, Error>;
  loadingComponent?: React.ReactNode;
  children: (data: T) => React.ReactNode;
}

export function QueryState<T>({
  query,
  loadingComponent,
  children,
}: QueryStateProps<T>): React.ReactNode {
  if (query.isPending) {
    return (
      loadingComponent || (
        <div
          role="status"
          aria-live="polite"
          aria-label="Loading statutory data"
          style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '20px 0' }}
        >
          <Skeleton height={28} width="30%" />
          <Skeleton height={80} width="100%" />
          <Skeleton height={140} width="100%" />
        </div>
      )
    );
  }

  if (query.isError) {
    const err = query.error;
    return (
      <div style={{ padding: '20px 0' }}>
        <Alert variant="danger" title="Failed to load statutory data">
          <p style={{ margin: '8px 0' }}>{err.message}</p>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              void query.refetch();
            }}
          >
            Retry
          </Button>
        </Alert>
      </div>
    );
  }

  return children(query.data);
}
